/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


let array1 = [9470190584093,9470328373533,9470266147101];
if (productIds.length) {
  array1 = productIds;
}
//const productLimitIds;
// New array for quantity limits
console.log(productLimitIds)
let arrayLimit = productLimitIds; 
if (productLimitIds.length) {
  arrayLimit = productLimitIds;
}

let totalLimit = 5;
if(totalLimit > 0){
  totalLimit = productLimit;
}

const cartJson = '/cart.js';
const array2 = [];
const isValuePresent = false;
function getCartJson() {
  //console.log('getCartJson - works')
  const cartAlert = `<div class="quick-drawer-banner quick-add_alert">
                        <span class="icon">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 0C12.4062 0 16 3.59375 16 8C16 12.4375 12.4062 16 8 16C3.5625 16 0 12.4375 0 8C0 3.59375 3.5625 0 8 0ZM7.25 4.75V8.75C7.25 9.1875 7.5625 9.5 8 9.5C8.40625 9.5 8.75 9.1875 8.75 8.75V4.75C8.75 4.34375 8.40625 4 8 4C7.5625 4 7.25 4.34375 7.25 4.75ZM8 12.5C8.53125 12.5 8.96875 12.0625 8.96875 11.5312C8.96875 11 8.53125 10.5625 8 10.5625C7.4375 10.5625 7 11 7 11.5312C7 12.0625 7.4375 12.5 8 12.5Z" fill="#00A4FF"/>
                          </svg>
                        </span>
                        <span>Raiven Marketplace Discount Code required at checkout to purchase.</span>
                        <a class="quick_drawer_more" href="#">Learn more</a>
                    </div>`;
  const cartAlertContent = `<div class="learn_more_overlay" style="display:none"></div><div class="learn_more_reservation" style="display:none">
                              <div class="lmr_content_wrap">
                                <div class="lmr_hd">
                                  <h2 class="h5 mb-0">Raiven Marketplace Discount Code</h2>
                                  <span class="lmr_close_btn">
                                    <svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" fill="none" fill-rule="evenodd" stroke-linejoin="round" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M5 19 19 5M5 5l14 14"></path></svg>
                                  </span>
                                </div>
                                <div class="lmr_content">
                                  <div class="lmr_heighlight" style="display:none">Apply your "Reservation Code" as a Discount Code at Checkout.</div>
                                  
                                  <h3 class="h6">How do I find my Discount Code?</h3>
                                  <ol>
                                    <li>Log in to <a href="http://app.qmerit.com" target="_blank">app.qmerit.com</a></li>
                                    <li>From the <strong>Active</strong> tab, navigate to your <strong>Activities</strong></li>
                                    <li>Click on an activity for a Cybertruck Powershare Promo installation to view the details on the Info tab</li>
                                    <li>Copy your Raiven Marketplace Discount Code</li>
                                    <li>Paste the Code into the "Discount Code" field during checkout, click Apply, and the price will change to $0</li>
                                  </ol>
                                  <h3 class="h6">Get a Raiven Marketplace Discount Code when you join Qmerit</h3>
                                  <p>To participate in the Powershare Promo, you must be a Qmerit Certified Installer or Solutions Partner and completed the Powershare training.</p>
                                  <ul>
                                    <li>If you are not part of the Qmerit Network - <a href="https://qmerit.com/ev-charging-stations-installer-network/" target="_blank">Join Today!</a></li>
                                    <li>If you are part of the Qmerit Network, for training and powershare installation documentation, please visit the <a href="https://resourcecenter.qmerit.com/" target="_blank">Qmerit Resource Center</a>.</li>
                                  </ul>
                                </div>
                              </div>
                            </div>`;
  
  fetch(cartJson).then((res) => {
      return res.json();
    }).then((data) => {
      localStorage.setItem('cart', JSON.stringify(data));
      const items = data.items;
      const array2 = [];
      
      let arrayLimitItemsQty = 0;
    
      for (let i = 0; i < items.length; i++) {
        console.log(items[i].id);
        array2.push(items[i].product_id);
        
        if (arrayLimit.includes(items[i].product_id)) {
          arrayLimitItemsQty += items[i].quantity;
        }
      }
      
      const isValuePresent = array1.some(value => array2.includes(value));
      
      const exceedsLimit = arrayLimitItemsQty >= totalLimit;
      console.log(`Equals or exceeds ${totalLimit} item limit:`, exceedsLimit);
      
      var proId = Number($('[name="product-id"]').val());
      const cartPage = window.location.pathname;
      //console.log(cartPage)
      
      if(cartPage.indexOf('/cart') > -1){
        //proId = Number($('tr[data-product-id]').data('productId'));
        //console.log('this is testing')
      }
      console.log('product id: '+proId);
      
      const remainingAllowance = Math.max(0, totalLimit - arrayLimitItemsQty);
      console.log('Remaining allowance:', remainingAllowance);
      
      arrayLimit.forEach(productId => {
        // Find the current quantity
        let currentProductQty = 0;
        for (let i = 0; i < items.length; i++) {
          if (items[i].product_id === productId) {
            currentProductQty = items[i].quantity;
            break;
          }
        }
        
        const maxAllowed = currentProductQty + remainingAllowance;
        console.log(`Product ${productId} - current: ${currentProductQty}, max allowed: ${maxAllowed}`);
        
        $(`tr[data-product-id="${productId}"] .qty-input__input`).attr({
          'max': maxAllowed,
          'min': 1
        }).removeClass('restict_qty'); // Remove readonly restriction
        
        if (exceedsLimit) {
          $(`tr[data-product-id="${productId}"] .qty-input__input`).attr('readonly', 'readonly').addClass('restict_qty');
          $(`tr[data-product-id="${productId}"] .qty-input button`).addClass('restict_qty_cart');
        } else {
          $(`tr[data-product-id="${productId}"] .qty-input button`).removeClass('restict_qty_cart');
        }
        
      });
    
      if(arrayLimit.indexOf(proId) > -1){
        const remainingAllowance = Math.max(0, totalLimit - arrayLimitItemsQty);
        
        let currentProductQty = 0;
        for (let i = 0; i < items.length; i++) {
          if (items[i].product_id === proId) {
            currentProductQty = items[i].quantity;
            break;
          }
        }
        
        const maxAllowed = remainingAllowance;
        //console.log(`Product page ID ${proId} - current: ${currentProductQty}, max allowed: ${maxAllowed}`);
        
        $('.product-info__add-to-cart .qty-input__input').attr({
          'max': maxAllowed,
          'min': 1
        }).removeClass('restict_qty');
        $('.product-info__add-to-cart .qty-input button').removeClass('restict_btn');
        
        if (exceedsLimit) {
          $('.product-info__add-to-cart .qty-input__input').attr('readonly', 'readonly').addClass('restict_qty');
          $('.product-info__add-to-cart .qty-input button').addClass('restict_btn');
          $('.product-info__add-button button.product-info__add-btn').addClass('restrict_to_cart');
          console.log('items exceeded limit: '+proId);
        } else {
          $('.product-info__add-button button.product-info__add-btn').removeClass('restrict_to_cart');
          console.log('item under limit: '+proId);
        }
        
      }
      
      if(exceedsLimit) {
        arrayLimit.forEach(productId => {
          $(`product-card[data-prod-id="${productId}"] button.product-info__add-btn`).addClass('restrict_to_cart');
        });
      } else {
        arrayLimit.forEach(productId => {
          $(`product-card[data-prod-id="${productId}"] button.product-info__add-btn`).removeClass('restrict_to_cart');
        });
      }
      
      // Show alert: array1 products
      if(isValuePresent){
        const alertLen = $('.quick-drawer-banner').length;
        if(alertLen == 0){
          const arrayLen = array2.length;
          if(arrayLen > 1){
            // Append cart alert to all product rows
            array1.forEach(productId => {
              if (array2.includes(Number(productId))) {
                $(`tr[data-product-id="${productId}"] .cart-item__details`).append($(cartAlert));
              }
            });
          }else{
            if(cartPage.indexOf('/cart') > -1){
              array1.forEach(productId => {
                if (array2.includes(Number(productId))) {
                  $(`tr[data-product-id="${productId}"] .cart-item__details`).append($(cartAlert));
                  
                }
              });
              
            }else{
              $(cartAlert).insertAfter('cart-warning-banner');
            }
          }
          
          $('cart-drawer').append($(cartAlertContent));
          $('body.template-cart').append($(cartAlertContent));
        }
        
      }else{ 
        console.log('No array1 products in cart');
        $('.quick-drawer-banner').hide();
      }
    });
    $(document).on('click','button.restict_qty_cart', function (event) {
      event.preventDefault();
      cartWarningBnr();
    });
    $('.learn_more_reservation').slideUp();
}
function handleAdditionalInfo() {
  const cartJson = '/cart.js';
  
  fetch(cartJson)
    .then((res) => res.json())
    .then((data) => {
      const items = data.items;
      const cartProductIds = items.map(item => item.product_id);
      
      const isAnyArray1ProductInCart = arrayLimit.some(productId => 
        cartProductIds.includes(productId)
      );
      
      const additionalInfoSection = $('.addtional_information');
      const inputFields = $('.addtional_information input[type="text"], #achknowledge_required');
      
      if (isAnyArray1ProductInCart) {
        additionalInfoSection.show();
        inputFields.prop('required', true);
        inputFields.addClass('required');
      } else {
        additionalInfoSection.hide();
        inputFields.prop('required', false);
        inputFields.removeClass('required');
        
        $('.addtional_information input[type="text"]').val('');
        $('#achknowledge_required').prop('checked', false);
      }
    })
    .catch(error => {
      console.error('Error fetching cart data:', error);
    });
}

$(document).ready(function() {
  handleAdditionalInfo();
});

document.addEventListener('on:line-item:change', handleAdditionalInfo);

function cartWarningBnr() {
  $('cart-warning-banner').removeClass('hidden');
  $('cart-warning-banner span.js-warning-message').text(`Maximum of ${totalLimit} Tesla products can be added to your Cart in total.`);
  $('cart-warning-banner .cart_banner').css({'color':'#FF3D00','background':'rgba(255, 61, 0, 0.2)'});
}
setTimeout(function () {
  getCartJson();  
},300)
document.addEventListener('on:line-item:change', (event) => {
  getCartJson();
});
document.addEventListener('on:cart-drawer:after-open', (event) => {
  getCartJson();
  handleAdditionalInfo();
});
document.addEventListener('on:cart-drawer:after-close', (event) => {
  setTimeout(function(){
    getCartJson();
  },500)
});

$(document).on('click','cart-drawer .quick_drawer_more',function (event) {
  event.preventDefault();
  $('.learn_more_overlay').show();
  $('.learn_more_reservation').slideDown();
})
$(document).on('click','cart-drawer .lmr_close_btn, cart-drawer .learn_more_overlay',function (event) {
  event.preventDefault();
  $('.learn_more_overlay').hide();
  $('.learn_more_reservation').slideUp();
});
$(document).on('click','.template-cart .quick_drawer_more',function (event) {
  event.preventDefault();
  $('.learn_more_overlay').show();
  $('.learn_more_reservation').show();
  $('body').addClass('learn_more_active');
})
$(document).on('click','.template-cart .lmr_close_btn, .template-cart .learn_more_overlay',function (event) {
  event.preventDefault();
  $('.learn_more_overlay').hide();
  $('.learn_more_reservation').hide();
  $('body').removeClass('learn_more_active');
});

$(document).on('click','button.product-info__add-btn.restrict_to_cart', function (event) {
  event.preventDefault();
  const cartDrawer = document.querySelector('cart-drawer');
  cartDrawer.open();
  cartWarningBnr();
  
})

$('span.close_btn').click(function () {
  $('.cart-alert-top').slideUp();
})
//a[data-discount]
$(document).on('click','a.discount-remove',function (event) {
  event.preventDefault();
  clearDiscount();
  
});

function clearDiscount() {
  //clearLocalStorage();
  fetch("/discount/CLEAR").then(function(response) {
      console.log(response);
      
    });
  setTimeout(function () {
    $('a.discount-remove + a.js-remove-item')[0].click()
    console.log('this is done');
  },500)
}

function clearLocalStorage() {
  localStorage.removeItem("discountCode");
}

//Custom Discount code